import { apiGet, apiPost, apiDelete, apiPatch } from '../../utilities/apiClient';
import {
  runAppleRemindersSync,
  getAppleRemindersCandidates,
  importAppleRemindersSelection,
  type AppleReminderItem,
} from '../../utilities/appleRemindersSync';
import {
  ADD_REMINDERS,
  FETCHING_REMINDERS,
  FETCHED_REMINDERS,
  PREPEND_REMINDER,
  CANCEL_REMINDER,
  COMPLETE_REMINDER,
  UPDATE_REMINDER,
} from '../constants';
import { getSessionIds, getStoreState } from './_sessionIds';

export interface RecurrenceRule {
  type: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly' | 'interval';
  time?: string; // HH:MM — not used for 'interval' type
  interval_hours?: number; // used for 'interval' type
  days?: string[]; // ['mon','wed','fri'] for weekly
  start_date?: string; // ISO date (YYYY-MM-DD) required for biweekly / monthly / quarterly / yearly
  smart_key?: string; // opaque key used by SmartRemindersScreen to identify toggle state
  page_id?: string; // survey page linked to this reminder — used for notification deep-link detail sheet
}


/** Fetch all reminders for a project. Skips if fetched within 60 seconds unless overridden. */
export const getReminders = (
  projectId: number | string,
  override = false,
) => async (dispatch: any): Promise<{ success: boolean; payload?: any[] }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId || projectId == null) return { success: false };

  const state = getStoreState() as any;
  const lastFetched = state.reminders?.fetched?.[String(projectId)] ?? 0;
  if (Date.now() - lastFetched < 60_000 && !override) {
    return { success: true, payload: state.reminders?.list?.[String(projectId)] ?? [] };
  }

  dispatch({ type: FETCHING_REMINDERS, payload: true });
  try {
    const res = await apiGet(`/prompts/get-reminders/${projectId}/${accountId}/${cognitoId}`);
    const list: any[] = Array.isArray(res?.payload) ? res.payload : [];
    dispatch({ type: ADD_REMINDERS, payload: { project_id: projectId, list } });
    dispatch({ type: FETCHED_REMINDERS, payload: { project_id: projectId } });
    return { success: true, payload: list };
  } catch {
    dispatch({ type: FETCHING_REMINDERS, payload: false });
    return { success: false };
  }
};

/** Create a one-off or recurring reminder. */
export const setReminder = (
  projectId: number | string,
  message: string,
  remindAt: string,
  recurrenceRule?: RecurrenceRule,
) => async (dispatch: any): Promise<{ success: boolean; payload?: any }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiPost(
      `/prompts/set-reminder/${projectId}/${accountId}/${cognitoId}`,
      { message, remind_at: remindAt, recurrence_rule: recurrenceRule },
    );
    if (res?.success && res.payload) {
      dispatch({ type: PREPEND_REMINDER, payload: { project_id: projectId, reminder: res.payload } });
    }
    return res ?? { success: false };
  } catch {
    return { success: false };
  }
};

/**
 * Trigger an Apple Reminders sync for a project and refresh the Redux list.
 * fullSync = true forces re-import of all items (ignores last-sync timestamp).
 */
export const syncAppleReminders = (
  projectId: number | string,
  fullSync = false,
) => async (dispatch: any): Promise<{ success: boolean; synced: number; skipped: boolean; noLists?: boolean }> => {
  try {
    const result = await runAppleRemindersSync(String(projectId), fullSync);
    if (result.synced > 0) {
      await dispatch(getReminders(projectId, true));
    }
    return { success: true, synced: result.synced, skipped: result.skipped, noLists: result.noLists };
  } catch {
    return { success: false, synced: 0, skipped: false };
  }
};

export const fetchAppleRemindersCandidates = () => async (): Promise<{
  success: boolean;
  reminders: AppleReminderItem[];
  skipped: boolean;
  noLists?: boolean;
}> => {
  try {
    const result = await getAppleRemindersCandidates();
    return {
      success: true,
      reminders: result.reminders,
      skipped: result.skipped,
      noLists: result.noLists,
    };
  } catch {
    return { success: false, reminders: [], skipped: false };
  }
};

export const importSelectedAppleReminders = (
  projectId: number | string,
  reminders: AppleReminderItem[],
) => async (dispatch: any): Promise<{
  success: boolean;
  synced: number;
  skipped: boolean;
}> => {
  try {
    const result = await importAppleRemindersSelection(String(projectId), reminders);
    if (result.synced > 0) {
      await dispatch(getReminders(projectId, true));
    }
    return {
      success: true,
      synced: result.synced,
      skipped: result.skipped,
    };
  } catch {
    return { success: false, synced: 0, skipped: false };
  }
};

/** Mark a scheduled reminder as manually completed. */
export const markReminderComplete = (
  promptId: number | string,
  projectId: number | string,
) => async (dispatch: any): Promise<{ success: boolean }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiPatch(
      `/prompts/complete-reminder/${promptId}/${projectId}/${accountId}/${cognitoId}`,
      {},
    );
    if (res?.success) {
      dispatch({ type: COMPLETE_REMINDER, payload: { project_id: projectId, reminder_id: promptId } });
    }
    return res ?? { success: false };
  } catch {
    return { success: false };
  }
};

/** Edit a scheduled reminder's message and/or scheduled time. */
export const updateReminder = (
  promptId: number | string,
  projectId: number | string,
  updates: { message?: string; remind_at?: string },
) => async (dispatch: any): Promise<{ success: boolean; payload?: any }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiPatch(
      `/prompts/update-reminder/${promptId}/${projectId}/${accountId}/${cognitoId}`,
      updates,
    );
    if (res?.success && res.payload) {
      dispatch({
        type: UPDATE_REMINDER,
        payload: { project_id: projectId, old_id: promptId, reminder: res.payload },
      });
    }
    return res ?? { success: false };
  } catch {
    return { success: false };
  }
};

/** Cancel a scheduled reminder by prompt ID. */
export const cancelReminder = (
  promptId: number | string,
  projectId: number | string,
) => async (dispatch: any): Promise<{ success: boolean }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    const res = await apiDelete(
      `/prompts/cancel-reminder/${promptId}/${projectId}/${accountId}/${cognitoId}`,
    );
    if (res?.success) {
      dispatch({ type: CANCEL_REMINDER, payload: { project_id: projectId, reminder_id: promptId } });
    }
    return res ?? { success: false };
  } catch {
    return { success: false };
  }
};
